/* eslint-disable @typescript-eslint/camelcase */
import { Grid, makeStyles, Tooltip } from '@material-ui/core';
import {
  Computer as ComputerIcon,
  DeviceUnknown as DeviceUnknownIcon,
  PhoneIphone as PhoneIcon,
  Tablet as TabletIcon,
} from '@material-ui/icons';
import clsx from 'clsx';
import TableItems, {
  MenuActionItem,
  MenuActions,
  StatusIcon,
  TableCell,
  TableRow,
} from 'components/TableItems';
import { TableCellHeaderProps } from 'components/TableItems/interfaces';
import Permission from 'components/ValidPermission';
import { Query, useTableFilters } from 'hooks/useTableFilters';
import i18n from 'i18n';
import { PermissionsData } from 'modules/authenticate/interfaces';
import {
  deleteTrustedDeviceRequest,
  getUserTrustedDevicesRequest,
} from 'modules/userTrustedDevices/actions';
import {
  FirstValidatedWithEnum,
  TrustedDeviceFields,
  TrustedDeviceResponse,
} from 'modules/userTrustedDevices/interfaces';
import React from 'react';
import { connect } from 'react-redux';
import ApplicationState from 'store/interfaces';
import { formatDateValue } from 'utils/dateFormat';
import ConfirmDisableDeviceDialog from './ConfirmDisableDeviceDialog';
import styles from './styles';

const useStyles = makeStyles(styles);

const osIcons = {
  Linux: '/images/linux-icon.svg',
  Tizen: '/images/tizen-icon.svg',
  'Mac OS X': '/images/mac-icon.svg',
  Ubuntu: '/images/ubuntu-icon.svg',
  'Chrome OS': '/images/chrome-icon.svg',
  Windows: '/images/windows-icon.svg',
  Android: '/images/android-icon.svg',
  iOS: '/images/ios-icon.svg',
  Others: '/images/others-os-icon.svg',
};

const getValidationMethodText = (
  validationMethod: FirstValidatedWithEnum
): string =>
  ({
    [FirstValidatedWithEnum.email_pin]: i18n.t('EMAIL_PIN_VALIDATION_KEY'),
    [FirstValidatedWithEnum.adm_pin]: i18n.t('ADM_PIN_VALIDATION_KEY'),
    [FirstValidatedWithEnum.zero_trust_allowlist]: i18n.t(
      'ZERO_TRUST_ALLOWLIST_VALIDATION_KEY'
    ),
    [FirstValidatedWithEnum.ip_history]: i18n.t('IP_HISTORY_VALIDATION_KEY'),
    [FirstValidatedWithEnum.password_reset]: i18n.t(
      'PASSWORD_RESET_VALIDATION_KEY'
    ),
    [FirstValidatedWithEnum.auto_register_next_device]: i18n.t(
      'AUTO_REGISTER_NEXT_DEVICE_VALIDATION_KEY'
    ),
  }[validationMethod] || i18n.t('NOT_FOUND_KEY'));

const requiredActionsPermissions: Array<keyof PermissionsData> = [
  'disable_user_devices',
];

interface TrustedDevicesListProps {
  userUuid: string;
  trustedDevicesList: TrustedDeviceResponse;
  initialQuery?: Query;
  isLoading: boolean;
  isDeleting: boolean;
  permissions: PermissionsData | undefined;
  getUserTrustedDevicesRequest: (props) => void;
  deleteTrustedDeviceRequest: (props) => void;
}

const TrustedDevicesList = ({
  userUuid,
  trustedDevicesList,
  initialQuery = { offset: 0, limit: 10, search: '' },
  isLoading,
  isDeleting,
  permissions,
  getUserTrustedDevicesRequest,
  deleteTrustedDeviceRequest,
}: TrustedDevicesListProps) => {
  const classes = useStyles();
  const [deviceToDelete, setDeviceToDelete] = React.useState<string | null>(
    null
  );

  const {
    itemOrder,
    query,
    handleLimitChange,
    handleOrder,
    handlePageChange,
  } = useTableFilters(initialQuery);

  const headers: TableCellHeaderProps[] = [
    {
      key: 'device_type',
      i18n: 'DEVICE_TYPE_KEY',
      order: false,
      style: { width: '7%' },
    },
    {
      key: 'first_validated_with',
      i18n: 'REASON_KEY',
      order: false,
      style: { width: '15%' },
    },
    {
      key: 'browser',
      i18n: 'BROWSER_KEY',
      order: false,
      style: { width: '16%' },
    },
    {
      key: 'created_at',
      i18n: 'CREATED_AT_KEY',
      order: false,
      style: { width: '16%' },
    },
    {
      key: 'last_validated_at',
      i18n: 'LAST_ACCESS_SINGULAR_KEY',
      order: false,
      style: { width: '16%' },
    },
    {
      key: 'last_validated_ip',
      i18n: 'LAST_VALIDATED_IP_KEY',
      order: false,
      style: { width: '16%' },
    },
    {
      key: 'enabled',
      i18n: 'STATUS_KEY',
      order: false,
      centerTitle: true,
      style: { width: '7%' },
    },
    {
      key: 'actions',
      i18n: 'ACTIONS_KEY',
      order: false,
      centerTitle: true,
      textAlignCenter: true,
      style: { width: '7%' },
      hidden:
        !!permissions &&
        requiredActionsPermissions.every(
          permission => !permissions[permission]
        ),
    },
  ];

  const getDeviceIcon = (userAgent: string) => {
    const ua = userAgent?.toLowerCase() || '';
    if (
      ua.includes('mobile') ||
      ua.includes('phone') ||
      ua.includes('android') ||
      ua.includes('iphone')
    ) {
      return <PhoneIcon />;
    }
    if (ua.includes('tablet') || ua.includes('ipad')) {
      return <TabletIcon />;
    }
    if (
      ua.includes('macintosh') ||
      ua.includes('windows') ||
      ua.includes('linux') ||
      ua.includes('desktop')
    ) {
      return <ComputerIcon />;
    }
    return <DeviceUnknownIcon />;
  };

  const getOSIcon = (osName: string) => {
    const iconPath = osIcons[osName] || osIcons.Others;
    return <img src={iconPath} alt={osName} className={classes.osIconImage} />;
  };

  const getTrustedDevices = () => {
    getUserTrustedDevicesRequest({
      userUuid,
      query,
    });
  };

  const handleDeleteDevice = (deviceUuid: string) => {
    deleteTrustedDeviceRequest({
      userUuid,
      devices: [deviceUuid],
      query,
    });
    setDeviceToDelete(null);
  };

  React.useEffect(() => {
    getTrustedDevices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const getMenuActionItems = (item: TrustedDeviceFields) => {
    return [
      {
        key: 'btn-disable-user-device',
        label: i18n.t('DELETE_TRUSTED_DEVICE_KEY'),
        onClick: () => setDeviceToDelete(item.device_uuid),
        disable: isDeleting,
        hidden: !item?.enabled || !permissions?.disable_user_devices,
      },
    ].filter(action => !action.hidden);
  };

  return (
    <Grid container>
      <TableItems
        header={
          <>
            {headers.map(item => (
              <TableCell
                textAlignCenter={item?.textAlignCenter}
                centerTitle={item?.centerTitle}
                key={item?.i18n}
                order={item?.order}
                style={item?.style}
                markOderItem={itemOrder === item?.key}
                handleOrder={() =>
                  item?.order ? handleOrder(item?.key) : null
                }
              >
                {i18n.t(item?.i18n)}
              </TableCell>
            ))}
          </>
        }
        renderItems={(item: TrustedDeviceFields, index) => (
          <TableRow key={item?.device_uuid} className={clsx(classes.tableRow)}>
            <TableCell className={classes.deviceIconCell}>
              <Grid container direction="row">
                {getDeviceIcon(item?.user_agent)}
                {item?.os && (
                  <Grid item className={classes.osIcon}>
                    <Tooltip
                      placement="top"
                      title={`${i18n.t('OPERATING_SYSTEM_KEY')} ${item.os}`}
                    >
                      {getOSIcon(item.os)}
                    </Tooltip>
                  </Grid>
                )}
              </Grid>
            </TableCell>
            <TableCell
              ellipsis
              tooltip={getValidationMethodText(item.first_validated_with)}
            >
              {getValidationMethodText(item.first_validated_with)}
            </TableCell>
            <TableCell ellipsis>
              <Tooltip placement="top" title={item?.user_agent || '--'}>
                <span>{item?.browser || '--'}</span>
              </Tooltip>
            </TableCell>
            <TableCell>{formatDateValue(item?.created_at)}</TableCell>
            <TableCell>{formatDateValue(item?.last_validated_at)}</TableCell>
            <TableCell ellipsis>
              <span>{item?.last_validated_ip || '--'}</span>
            </TableCell>
            <TableCell
              className={classes.centerSmallFields}
              tooltip={i18n.t(item?.enabled ? 'ENABLED_KEY' : 'DISABLED_KEY')}
            >
              <Grid container justify="center">
                <StatusIcon status={item?.enabled ? 'success' : 'inactive'} />
              </Grid>
            </TableCell>

            <Permission value={requiredActionsPermissions}>
              <TableCell textAlignCenter>
                <MenuActions id={`btn-options-apps-${index}`}>
                  {getMenuActionItems(item)?.length > 0 ? (
                    getMenuActionItems(item)?.map(action => (
                      <MenuActionItem
                        key={action?.key}
                        id={action?.key}
                        onClick={action?.onClick}
                      >
                        {action?.label}
                      </MenuActionItem>
                    ))
                  ) : (
                    <MenuActionItem>
                      {i18n.t('NO_ACTIONS_AVAILABLE_KEY')}
                    </MenuActionItem>
                  )}
                </MenuActions>
              </TableCell>
            </Permission>
          </TableRow>
        )}
        data={trustedDevicesList}
        isLoading={isLoading || isDeleting}
        onChangePage={handlePageChange}
        onChangeLimit={handleLimitChange}
        overrideRowsCount={query.limit}
        notFoundText={i18n.t('NO_TRUSTED_DEVICES_KEY')}
      />

      <ConfirmDisableDeviceDialog
        open={!!deviceToDelete}
        onClose={() => setDeviceToDelete(null)}
        onConfirm={() => {
          if (deviceToDelete) handleDeleteDevice(deviceToDelete);
        }}
      />
    </Grid>
  );
};

const mapStateToProps = ({
  authenticate: { data },
  userTrustedDevices: {
    trustedDevicesList,
    isLoadingTrustedDevicesList,
    isDeleting,
  },
}: ApplicationState) => ({
  trustedDevicesList,
  isLoading: isLoadingTrustedDevicesList,
  isDeleting,
  permissions: data?.permissions,
});

export default connect(mapStateToProps, {
  getUserTrustedDevicesRequest,
  deleteTrustedDeviceRequest,
})(TrustedDevicesList);
